import { BUTTON_STYLES, SHADOWS, SHAPE_STYLES, TEXT_STYLE } from './constants';
import { t } from './localizationController';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  createOpacityAnimation,
  createTransformAnchoredAnimation,
  getAbsolutePosAndScale,
  getEntityState,
  getEntityStateAndProgress,
  getObjectsByType,
  getZeroStaticEntity,
  moveEntityToFront,
  setEntitySize,
  setParent,
} from './entity';
import {
  addV2,
  Ease,
  hasVisibleState,
  lerpSize,
  removeDenseArrayIndex,
  secondsToTimeString,
  toSize,
  v2,
} from './helpers';
import { debugState, dt, frameNr, time } from './index';
import {
  EventClientToServer,
  JSON_VideoSyncAction,
  ServerEvent_EntityProps,
  ServerEvent_VideoPlayer,
  ObjectType,
  Side,
  Alignment_Horizontal,
} from './shared/api_shared_types';
import { setVisibilityState, setParentByEntityProps } from './simulate';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import {
  AnimationType,
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  MoveMode,
  PlayState,
  RenderItemType,
  Shape,
  TextAppearStyle,
  VideoPlayer,
  VideoPlayerSizeState,
  type Container,
  type Size,
  type SizeAnimation,
  type V2,
  type TextObjectStyle,
} from './types';
import { createAndAddLocalIconButton } from './icon_button';
import { createAndAddSlider, setSliderScaledValue, setSliderSecondaryScaledValue } from './slider';
import { createImageEnt } from './image_entity';
import * as DataController from './controllers/data_controller';
import { createAndAddLocalContainer, setContainerStyle, setContainerStyles } from './container';
import { createDomObject } from './dom_object';
import Hls, * as HLS from 'hls.js';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageClearRect,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import * as Background from './background_controller';
import * as LocalSettingsController from './localSettingsController';
import { hideToolTip, showStickyToolTip } from './controllers/tooltip_controller';

const SIZE_MINIMIZED = 200 as const;
const DEFAULT_ASPECT = 16 / 9; //Real aspect is set when video data is loaded
const CORNER_RADIUS = 15;

const TEXT_TIME: TextObjectStyle = {
  ...TEXT_STYLE.DEFAULT_TRANSPARANT,
  color: COLORS.WHITE_25,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.7,
  doAutoResize: false,
  roundedSides: [true, true],
  textAlign: Alignment_Horizontal.CENTER,
  padding: [4, 1],
};

const SYNC_TOOLTIP_TEXT = `<b>Videos play synchronously</b>\nIf you play or pause, it plays or pauses for your partner too.`;

export function controller(): GameObjectController {
  return {
    getNew: getZeroVideoPlayer,
    sync,
    update,
    createPackage,
    dispose,
  };
}

export function getZeroVideoPlayer(ent: Entity): VideoPlayer {
  let videoObject: VideoPlayer = {
    ...ent,
    objectType: ObjectType.VIDEOPLAYER,
    domObj: null,
    videoUrl: '',
    captionsUri: '',
    parsedVTTCues: null,
    onVTTTimeUpdate: null,
    bottomContainer: null,
    elementID: '',
    element: null,
    videoElement: null,
    subsElement: null,
    playState: PlayState.NONE,
    hls: null,
    videoElementWidth: 0,
    videoElementHeight: 0,
    videoAspect: 16 / 9,

    showSyncPlayTip: false,

    controlsVisible: false,
    requestPlay: false,
    requestPause: false,
    requestPlayTime: -1,
    currentPlayTime: 0,
    playerReady: false,
    readyToPlayLocal: false,
    readyToPlaySync: false,
    loop: false,
    autoPlay: false,

    allowUserPlay: false,
    hideControls: false,
    buttonPlayPause: null,
    buttonSubs: null,
    btnMinimize: null,
    playSlider: null,
    duration: 0,
    previousPlayTime: -1,
    localPlayInterupted: false, //can we get rid of the bool now we have a timer?
    lastSeekTime: -1,
    syncedPlay: false,
    msg: '',
    msgDuration: 0,
    msgTime: 0,
    syncedPlayInterupted: false,
    defaultWidthPx: 3.6,
    msgTextObject: null,
    style: '',
    title: '',
    tObjTitle: null,
    tObjTime: null,
    imageUri: '',
    container: null,
    thumbnail: null,
    playIcon: null,
    playerSizeState: VideoPlayerSizeState.NONE,
    sizeAnimations: [],
    handleHLSError: null,
    retryLoadSourceTime: -1,
    localPlayInteruptedSynced: false,
    showSubs: false,
    showSubsTimeout: null,
  };

  return videoObject;
}

export function sync(
  manager: GameManager,
  videoPlayer: VideoPlayer,
  data: ServerEvent_VideoPlayer,
) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if ('uri' in data) {
    videoPlayer.videoUrl = data.uri;
  }

  if ('autoPlay' in data) {
    videoPlayer.autoPlay = data.autoPlay;
  }

  if ('showSyncVideoTip' in data) {
    videoPlayer.showSyncPlayTip = data.showSyncVideoTip;
  }

  if ('loop' in data) {
    videoPlayer.loop = data.loop;
  }

  if (data.hasOwnProperty('style')) {
    if (
      data.style === '' ||
      data.style === 'default' ||
      data.style === 'polaroid' ||
      data.style === 'minimized'
    ) {
      videoPlayer.style = data.style;
    }
  }

  if (!videoPlayer.id) {
    addGameObject(manager, videoPlayer);
    const sizePxDefault = 550;

    videoPlayer.defaultWidthPx = data.sizePx || sizePxDefault;

    initVideoPlayer(manager, videoPlayer, data.uri);
    setParentByEntityProps(manager, videoPlayer, data.sId, data.roomId, entityProps);
    setVideoPlayerMessage(videoPlayer, 'Loading Video...');
  }

  // setGameObjectyEntityProps(videoPlayer, entityProps);

  if ('readyToPlay' in data) {
    if (videoPlayer.readyToPlaySync !== data.readyToPlay) {
      videoPlayer.readyToPlaySync = true;

      checkReadyToPlay(videoPlayer);
    }
  }

  if ('imageUri' in data) {
    if (videoPlayer.imageUri !== data.imageUri) {
      setThumbnail(videoPlayer, data.imageUri);
    }
  }

  if ('showSubtitles' in data) {
    videoPlayer.showSubs = data.showSubtitles;
  }

  if ('captionsUri' in data) {
    if (videoPlayer.captionsUri !== data.captionsUri) {
      videoPlayer.captionsUri = data.captionsUri;

      getSubs(videoPlayer.captionsUri).then((vttText) => {
        addSubsToVideo(videoPlayer, vttText);
      });
    }
  }

  if ('title' in data) {
    setTitle(videoPlayer, data.title);
  }

  if ('currentState' in data || 'currentTime' in data) {
    let state = videoPlayer.playState === PlayState.PLAYING ? 'play' : 'pause';
    if ('currentState' in data && data.currentState.length) {
      state = data.currentState;
    }
    let time = videoPlayer.currentPlayTime;
    if ('currentTime' in data) {
      time = data.currentTime;
    }

    setVideoState(videoPlayer, state, time, '');
  }
}

function initVideoPlayer(
  manager: GameManager,
  videoPlayer: VideoPlayer,
  videoUrl: string,
): VideoPlayer {
  let videoEl = document.createElement('video') as HTMLVideoElement;
  videoPlayer.videoElement = videoEl;
  videoPlayer.videoElement.disablePictureInPicture = true;

  videoPlayer.handleHLSError = (e, data) => {
    handleHLSError(videoPlayer, e, data);
  };

  videoEl.classList.add('video-player', 'video-object');
  videoEl.id = 'player-' + crypto.randomUUID();

  const subsEl = document.createElement('div') as HTMLDivElement;
  videoPlayer.subsElement = subsEl;
  videoPlayer.subsElement.classList.add('subtitle-overlay');
  videoPlayer.subsElement.style.display = 'none';

  let width = videoPlayer.defaultWidthPx;
  let height = videoPlayer.defaultWidthPx / DEFAULT_ASPECT;

  if (videoPlayer.style === 'polaroid') {
    height = width;
  } else if (videoPlayer.style === 'minimized') {
    width = SIZE_MINIMIZED;
    height = SIZE_MINIMIZED;

    videoPlayer.playerSizeState = VideoPlayerSizeState.MINIMIZED;
  } else {
    videoPlayer.playerSizeState = VideoPlayerSizeState.DEFAULT;
  }

  let videoHeight = height;
  let videoWidth = videoHeight * DEFAULT_ASPECT;

  // videoEl.setAttribute('preload', 'none');

  let domObj = createDomObject(
    getZeroStaticEntity(),
    [videoEl, subsEl],
    manager.DOM.videoContainer,
    manager,
  );
  domObj.centerContent = true;
  domObj.cornerRadius = CORNER_RADIUS;

  setVisibilityState(domObj, VisibilityState.DEFAULT);

  videoPlayer.domObj = domObj;
  videoPlayer.element = domObj.wrapperEl;
  videoPlayer.elementID = videoEl.id;
  videoPlayer.videoUrl = videoUrl;
  videoPlayer.videoElementWidth = videoWidth;
  videoPlayer.videoElementHeight = videoHeight;

  videoPlayer.shape = Shape.RECT;
  videoPlayer.influenceKinetic = true;

  videoPlayer.width = width;
  videoPlayer.height = height;
  videoPlayer.clickable = true;
  videoPlayer.allowDrag = true;
  videoPlayer.allowHighlight = true;
  videoPlayer.passHover = true;
  videoPlayer.passClickCanvas = true;

  {
    const c = createAndAddLocalContainer(videoPlayer.manager, getZeroStaticEntity());
    setParent(c, videoPlayer, v2());
    c.relativeWidth = 1;
    c.relativeHeight = 1;
    c.cornerRadius = CORNER_RADIUS;
    c.doClipChildren = true;
    setContainerStyle(c, SHAPE_STYLES.NONE);

    videoPlayer.container = c;
  }

  {
    const c = createAndAddLocalContainer(videoPlayer.manager, getZeroStaticEntity());
    setParent(c, videoPlayer, v2());
    c.relativeWidth = 1;
    c.height = 80;
    c.cornerRadius1 = 0;
    c.cornerRadius2 = 0;
    c.cornerRadius3 = CORNER_RADIUS;
    c.cornerRadius4 = CORNER_RADIUS;
    // c.shapeStyle = SHAPE_STYLES.DEBUG;
    setContainerStyle(c, SHAPE_STYLES.VIDEOPLAYER.GRADIENT);
    c.anchoredPos = anchoredPos(v2(0, 1), v2(0, 0), v2(0, 1));

    videoPlayer.bottomContainer = c;
  }

  const isDebug = process.env.WC_ENV && process.env.WC_ENV === 'local';

  if (videoPlayer.videoUrl.substring(videoPlayer.videoUrl.length - 3) === 'mp4') {
    Background.setVideoBackground(manager, videoPlayer.videoUrl);
  } else {
    const config: Partial<HLS.HlsConfig> = {
      enableWorker: true,
      // Increase the maximum buffer length in seconds
      maxMaxBufferLength: 60, // Default is 600
      // backBufferLength: 90,
      lowLatencyMode: false,

      // The maximum size (in bytes) of the buffer.
      // More memory allows it to hold more data without eviction.
      // maxBufferSize: 60 * 1024 * 1024, // 60 MB
      // debug,
    };

    if (isDebug) {
      logger.videoplayer('HLS inited in debug mode');
    }

    videoPlayer.hls = new Hls(config);

    videoPlayer.hls.on(Hls.Events.ERROR, videoPlayer.handleHLSError);

    videoPlayer.hls.on(Hls.Events.MEDIA_ATTACHED, function () {});

    videoPlayer.hls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {});

    videoPlayer.hls.on(Hls.Events.FPS_DROP, function (event, data) {
      logger.videoplayer('hls fps drop ' + data);
    });
  }

  videoEl.addEventListener('canplay', (event) => {
    logger.videoplayer('html event: can play');
    if (!videoPlayer.readyToPlayLocal) {
      onVideoPlayerDataLoaded(videoPlayer);
    }
  });

  videoEl.addEventListener('canplaythrough', (event) => {
    logger.videoplayer('html event: canplaythrough');
  });

  videoEl.addEventListener('loadedmetadata', () => {
    logger.videoplayer('html event: loadedmetadata');
    onVideoPlayerMetaDataLoaded(videoPlayer);
  });

  videoEl.addEventListener('ended', () => {
    logger.videoplayer('html event: ended');
    onVideoPlayerEnded(videoPlayer);
  });

  videoEl.addEventListener('waiting', () => {
    logger.videoplayer('html event: waiting');

    if (!videoPlayer.localPlayInterupted && !videoPlayer.syncedPlayInterupted) {
      videoPlayer.localPlayInteruptedSynced = false;
      videoPlayer.localPlayInterupted = true;
      syncLocalPlayInterupted(videoPlayer);
    }
  });

  videoEl.addEventListener('pause', () => {
    // logger.videoplayer('html event: pause');
  });

  videoEl.addEventListener('playing', () => {
    logger.videoplayer('html event: playing');

    if (videoPlayer.localPlayInterupted) {
      videoPlayer.localPlayInterupted = false;
      const send: JSON_VideoSyncAction = {
        sId: videoPlayer.serverObjId,
        action: 'playContinued',
        time: videoPlayer.currentPlayTime,
      };
    }
  });

  if (videoPlayer.hls) {
    videoPlayer.hls.loadSource(videoUrl);
    videoPlayer.hls.attachMedia(videoEl);
  } else {
    videoPlayer.videoElement.src = videoUrl;
  }

  let appearStyle = TextAppearStyle.FADE;
  const msgText = createAndAddLocalTextObject(
    manager,
    getZeroStaticEntity(),
    videoPlayer.msg,
    TEXT_STYLE.VIDEOPLAYER_MSG,
    4,
    false,
    0,
    1,
    appearStyle,
  );

  msgText.onlyShowInDefaultState = false;
  setParent(msgText, videoPlayer);
  videoPlayer.msgTextObject = msgText;
  videoPlayer.msgTextObject.moveMode = MoveMode.ANCHORED;
  videoPlayer.msgTextObject.anchoredPos = anchoredPos(v2(), v2(20, 20), v2());

  setParent(domObj, videoPlayer, v2());

  setVisibilityState(videoPlayer, VisibilityState.APPEARING);

  updateDomSize(videoPlayer);

  return videoPlayer;
}

async function getSubs(url: string) {
  let response: Response = null;

  try {
    response = await fetch(url);
  } catch (e) {
    logger.error('Cannot fetch subsitle file', url, e);
  }

  if (response && response.ok) {
    let vttText: string = null;
    try {
      vttText = await response.text();
    } catch (e) {
      logger.error('cannot parse subtitle data', url, e);
      return null;
    }

    return vttText;
  }
}

type VTTCueParsed = { start: number; end: number; text: string };

function parseVTTTime(timeStr: string): number {
  const parts = timeStr.trim().split(':');
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseFloat(parts[2]);
  }
  return parseInt(parts[0]) * 60 + parseFloat(parts[1]);
}

function parseVTT(vttText: string): VTTCueParsed[] {
  const cues: VTTCueParsed[] = [];
  const blocks = vttText.replace(/\r\n/g, '\n').split(/\n\n+/);
  for (const block of blocks) {
    const lines = block.trim().split('\n');
    const timingIndex = lines.findIndex(l => l.includes('-->'));
    if (timingIndex === -1) {
      continue;
    }
    const timingLine = lines[timingIndex];
    const arrowIndex = timingLine.indexOf('-->');
    const startStr = timingLine.slice(0, arrowIndex).trim();
    const endStr = timingLine.slice(arrowIndex + 3).trim().split(' ')[0];
    const start = parseVTTTime(startStr);
    const end = parseVTTTime(endStr);
    const text = lines.slice(timingIndex + 1).join('\n').trim();
    if (text) {
      cues.push({ start, end, text });
    }
  }
  return cues;
}

function addSubsToVideo(videoPlayer: VideoPlayer, vttText: string) {
  if (videoPlayer.onVTTTimeUpdate) {
    videoPlayer.videoElement.removeEventListener('timeupdate', videoPlayer.onVTTTimeUpdate);
    videoPlayer.onVTTTimeUpdate = null;
  }

  videoPlayer.parsedVTTCues = parseVTT(vttText);

  const onVTTTimeUpdate = () => {
    const currentTime = videoPlayer.videoElement.currentTime;
    const cues = videoPlayer.parsedVTTCues;
    if (!cues) {
      return;
    }
    const activeCue = cues.find(c => currentTime >= c.start && currentTime <= c.end);
    if (activeCue) {
      videoPlayer.subsElement.innerHTML = `<span>${activeCue.text}</span>`;
    } else {
      videoPlayer.subsElement.innerHTML = '';
    }
  };

  videoPlayer.onVTTTimeUpdate = onVTTTimeUpdate;
  videoPlayer.videoElement.addEventListener('timeupdate', onVTTTimeUpdate);

  {
    videoPlayer.buttonSubs = createAndAddLocalIconButton(
      videoPlayer.manager,
      BUTTON_STYLES.SUBS_SHOW,
    );
    setParent(videoPlayer.buttonSubs, videoPlayer);
    videoPlayer.buttonSubs.anchoredPos = anchoredPos(v2(1, 1), v2(-30, -30), v2(0.5, 0.5));
    videoPlayer.buttonSubs.passHover = true;
    videoPlayer.buttonSubs.moveMode = MoveMode.ANCHORED;
    videoPlayer.buttonSubs.label = t('videoplayer.toggle_subtitles');
    videoPlayer.buttonSubs.labelSide = Side.ABOVE;

    setSubsVisibility(videoPlayer, videoPlayer.showSubs, false);
  }

  setVisibilityState(videoPlayer.buttonSubs, VisibilityState.HIDDEN);
}

function onVideoPlayerDataLoaded(videoPlayer: VideoPlayer) {
  if (videoPlayer.doKill) return;

  const videoW = videoPlayer.videoElement.videoWidth;
  const videoH = videoPlayer.videoElement.videoHeight;

  if (videoH > 0) {
    videoPlayer.videoAspect = videoW / videoH;

    videoPlayer.playState = PlayState.STOPPED;
  }

  if (
    videoPlayer.style === 'minimized' &&
    videoPlayer.playerSizeState === VideoPlayerSizeState.MINIMIZED
  ) {
    videoPlayer.videoElementWidth = videoPlayer.videoElementHeight * videoPlayer.videoAspect;
  } else {
    videoPlayer.videoElementHeight = videoPlayer.videoElementWidth / videoPlayer.videoAspect;
  }

  if (videoPlayer.playerSizeState === VideoPlayerSizeState.DEFAULT) {
    videoPlayer.height = videoPlayer.videoElementHeight;
  }

  const send: JSON_VideoSyncAction = {
    sId: videoPlayer.serverObjId,
    action: 'dataLoaded',
    time: videoPlayer.currentPlayTime,
  };


  videoPlayer.readyToPlayLocal = true;

  checkReadyToPlay(videoPlayer);
}

function onVideoPlayerMetaDataLoaded(videoPlayer: VideoPlayer) {
  if (videoPlayer.playerReady || videoPlayer.doKill) return;

  videoPlayer.playerReady = true;

  videoPlayer.duration = videoPlayer.videoElement.duration;

  setTitle(videoPlayer, videoPlayer.title);

  const manager = videoPlayer.manager;

  {
    const btn = createAndAddLocalIconButton(manager, BUTTON_STYLES.PLAY);
    setParent(btn, videoPlayer);
    btn.anchoredPos = anchoredPos(v2(0, 1), v2(30, -30), v2(0.5, 0.5));
    btn.passHover = true;
    btn.moveMode = MoveMode.ANCHORED;
    setVisibilityState(btn, VisibilityState.HIDDEN);
    videoPlayer.buttonPlayPause = btn;
  }

  const slider = createAndAddSlider(manager, '');
  setParent(slider, videoPlayer);
  slider.knob.radius = 0;
  setContainerStyles(slider.knob, SHAPE_STYLES.VIDEOPLAYER.KNOB, SHAPE_STYLES.VIDEOPLAYER.KNOB_HL);
  slider.maxBarWidth = 6;
  slider.barColorBack = [130, 130, 130, 80];
  slider.barColorSecond = [240, 240, 240, 130];
  slider.barColorThird = [256, 256, 256, 120];
  slider.useThirdBar = true;
  slider.barColorValue = COLORS.NEW_STYLE.BLUE_LIGHT;
  slider.relativeWidth = 1;
  slider.padWidth = -(70 + 70);
  slider.height = 30;
  slider.anchoredPos = anchoredPos(v2(0, 1), v2(70, -20), v2(0, 0.5));
  slider.passHover = true;
  slider.moveMode = MoveMode.ANCHORED;
  slider.min = 0;
  slider.max = videoPlayer.duration;
  setVisibilityState(slider, VisibilityState.HIDDEN);
  videoPlayer.playSlider = slider;

  const tObj = createAndAddLocalTextObject(manager, getZeroStaticEntity(), '00:00', TEXT_TIME);
  tObj.moveMode = MoveMode.ANCHORED;
  tObj.anchoredPos = anchoredPos(v2(1, 1), v2(-70, -28), v2(1, 1));
  setVisibilityState(tObj, VisibilityState.HIDDEN);
  videoPlayer.tObjTime = tObj;
  setParent(tObj, videoPlayer);
}

function onVideoPlayerEnded(videoPlayer: VideoPlayer) {
  videoPlayer.syncedPlayInterupted = false;
  videoPlayer.localPlayInterupted = false;

  videoPlayer.playState = PlayState.STOPPED;
  videoPlayer.buttonPlayPause.style = BUTTON_STYLES.PLAY;

  videoPlayer.requestPlayTime = 0;
  if (videoPlayer.thumbnail) {
    setVisibilityState(videoPlayer.thumbnail, VisibilityState.APPEARING);
    setVisibilityState(videoPlayer.domObj, VisibilityState.HIDING);
  }

  if (videoPlayer.tObjTitle) {
    setVisibilityState(videoPlayer.tObjTitle, VisibilityState.DEFAULT);
  }

  showGradient(videoPlayer.bottomContainer);

  if (videoPlayer.style === 'minimized') {
    minimize(videoPlayer);
  }

  const send: JSON_VideoSyncAction = {
    sId: videoPlayer.serverObjId,
    action: 'videoEnded',
    time: videoPlayer.currentPlayTime,
  };

  if (videoPlayer.playIcon && !videoPlayer.controlsVisible) {
    setVisibilityState(videoPlayer.playIcon, VisibilityState.APPEARING);
  }

}

function setVideoPlayerMessage(player: VideoPlayer, msg: string) {
  if (msg === player.msg) return;

  if (msg.length === 0) {
    setVisibilityState(player.msgTextObject, VisibilityState.HIDING);
  } else {
    changeTextObjectText(player.msgTextObject, msg);
    if (player.msg.length === 0) {
      setVisibilityState(player.msgTextObject, VisibilityState.APPEARING);
    }
  }

  player.msg = msg;
}

function update(player: VideoPlayer): boolean {
  let isDirty = false;

  let { state, progress } = getEntityStateAndProgress(player);

  const p = Ease.outCubic(progress);

  if (state === VisibilityState.APPEARING) {
    let SCALE = 0.5;

    player.scale = SCALE + p * SCALE;
    player.layoutChanged = true;
    isDirty = true;
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    let SCALE = 0.5;

    player.scale = SCALE + (1 - p) * SCALE;
    isDirty = true;

    player.layoutChanged = true;
  } else if (state === VisibilityState.DEFAULT) {
    if (player.scale !== 1) {
      player.scale = 1;
      player.layoutChanged = true;
      updateDomSize(player);
      isDirty = true;
    }
  }

  if (player.retryLoadSourceTime >= 0) {
    player.retryLoadSourceTime -= dt;

    if (player.retryLoadSourceTime <= 0) {
      player.hls.loadSource(player.videoUrl);
      // player.videoElement.src = player.videoUri;
      player.retryLoadSourceTime = -1;
    }
  }

  if (!player.playerReady) return false;

  for (let i = player.sizeAnimations.length - 1; i >= 0; i--) {
    if (player.sizeAnimations[i].doKill) {
      removeDenseArrayIndex(player.sizeAnimations, i);
    } else if (player.sizeAnimations[i].prop === 'size') {
      setEntitySize(
        player,
        player.sizeAnimations[i].currentValue.width,
        player.sizeAnimations[i].currentValue.height,
      );

      setEntitySize(player.playSlider, player.width - 100, 12);
    } else if (player.sizeAnimations[i].prop === 'videoSize') {
      player.videoElementWidth = player.sizeAnimations[i].currentValue.width;
      player.videoElementHeight = player.sizeAnimations[i].currentValue.height;

      if (player.thumbnail) {
        setEntitySize(
          player.thumbnail,
          player.videoElementHeight * player.thumbnail.aspect,
          player.videoElementHeight,
        );
        // player.thumbnail.height = player.videoElementHeight;
        // player.thumbnail.width = player.videoElementHeight * player.thumbnail.aspect;
      }
    }

    if (player.sizeAnimations[i].p === 1) {
      if (player.playerSizeState === VideoPlayerSizeState.TO_DEFAULT) {
        player.playerSizeState = VideoPlayerSizeState.DEFAULT;
      } else if (player.playerSizeState === VideoPlayerSizeState.TO_MINIMIZED) {
        player.playerSizeState = VideoPlayerSizeState.MINIMIZED;
      }
      player.sizeAnimations[i].doKill = true;
      removeDenseArrayIndex(player.sizeAnimations, i);
    }
  }

  if (player.playSlider.valueSetByUserThisFrame) {
    const newPlayTime = player.playSlider.valuePercentage * player.duration;

    player.localPlayInterupted = false;

    player.videoElement.currentTime = newPlayTime;
    player.lastSeekTime = time;
    if (player.thumbnail) {
      player.thumbnail.disappearDuration = 0;
      setVisibilityState(player.thumbnail, VisibilityState.HIDING);
      setVisibilityState(player.domObj, VisibilityState.APPEARING);
    }
    if (player.tObjTitle) {
      setVisibilityState(player.tObjTitle, VisibilityState.HIDDEN);
    }
    // if (player.playIcon) {
    //   if (hasVisibleState(player.playIcon.visibilityState)) {
    //     setVisibilityState(player.playIcon, VisibilityState.HIDING);
    //   }
    // }

    setVideoPlayerMessage(player, '');

    const send: JSON_VideoSyncAction = {
      sId: player.serverObjId,
      action:
        player.playState === PlayState.PAUSED || player.playState === PlayState.STOPPED
          ? 'userSeekPause'
          : 'userSeekPlay',
      time: newPlayTime,
    };

  }

  player.currentPlayTime = player.videoElement.currentTime;

  if (player.previousPlayTime !== player.currentPlayTime) {
    setSliderScaledValue(player.playSlider, player.currentPlayTime, true);

    const timeRemaining = player.duration - player.currentPlayTime;

    const friendlyTimeRemaining = secondsToTimeString(timeRemaining, true);
    changeTextObjectText(player.tObjTime, friendlyTimeRemaining);
  }

  if (player.localPlayInterupted) {
    const timeSinceLastSeek = time - player.lastSeekTime;
    if (timeSinceLastSeek > 0.5 && !player.localPlayInteruptedSynced) {
      syncLocalPlayInterupted(player);
    }
  }

  const buffered = player.videoElement.buffered;
  if (buffered) {
    //We search for the buffer which is relevant to the current play time
    for (let i = 0; i < buffered.length; i++) {
      const bufferStart = buffered.start(i);
      const bufferEnd = buffered.end(i);
      const bufferLength = bufferEnd - bufferStart;

      if (bufferStart < player.currentPlayTime && bufferEnd > player.currentPlayTime) {
        setSliderSecondaryScaledValue(player.playSlider, bufferEnd);
        break;
      }
    }
  }

  if (player.btnMinimize?.clickedThisFrame) {
    if (player.playState === PlayState.PLAYING) {
      player.requestPause = true;
      setVideoPlayerMessage(player, '');
      const send: JSON_VideoSyncAction = {
        sId: player.serverObjId,
        action: 'userPause',
        time: player.currentPlayTime,
      };

    }
    minimize(player, true);
  }

  if (player.buttonSubs?.clickedThisFrame) {
    const newShow = !player.showSubs;
    setSubsVisibility(player, newShow, true);
    for (const other of getObjectsByType<VideoPlayer>(player.manager, ObjectType.VIDEOPLAYER)) {
      if (other !== player && other.buttonSubs) {
        setSubsVisibility(other, newShow, false);
      }
    }
  }

  if (player.readyToPlaySync) {
    if (player.buttonPlayPause.clickedThisFrame || player.clickedThisFrame) {
      if (player.videoElement.paused) {
        player.requestPlay = true;
        setVideoPlayerMessage(player, '');
        if (player.currentPlayTime === player.videoElement.duration) {
          player.currentPlayTime = 0;
        }

        const send: JSON_VideoSyncAction = {
          sId: player.serverObjId,
          action: 'userPlay',
          time: player.currentPlayTime,
        };

        } else {
        player.requestPause = true;
        setVideoPlayerMessage(player, '');
        const send: JSON_VideoSyncAction = {
          sId: player.serverObjId,
          action: 'userPause',
          time: player.currentPlayTime,
        };

        }
    }
  }

  if (player.requestPlay) {
    player.requestPlay = false;

    player.videoElement.loop = player.loop;

    pauseAndMinimizeOtherVideoPlayers(player);
    if (player.thumbnail) {
      player.thumbnail.disappearDuration = 0;
      setVisibilityState(player.thumbnail, VisibilityState.HIDING);
      setVisibilityState(player.domObj, VisibilityState.APPEARING);
    }
    if (player.tObjTitle) {
      setVisibilityState(player.tObjTitle, VisibilityState.HIDDEN);
    }
    if (player.playIcon) {
      if (hasVisibleState(player.playIcon.visibilityState)) {
        setVisibilityState(player.playIcon, VisibilityState.HIDING);
      }
    }

    if (player.videoElement.paused) {
      // player.timeSinceStartPlaying = 0;
      player.localPlayInterupted = false;
      if (player.requestPlayTime >= 0) {
        handleRequestPlayTime(player);
      }
      playVideo(player);
    }
  }

  if (player.playState === PlayState.PLAYING && player.manager.paused) {
    player.requestPause = true;

    setVideoPlayerMessage(player, '');
    const send: JSON_VideoSyncAction = {
      sId: player.serverObjId,
      action: 'userPause',
      time: player.currentPlayTime,
    };

  }

  if (player.requestPause) {
    player.requestPause = false;

    if (!player.videoElement.paused) {
      player.localPlayInterupted = false;
      if (player.requestPlayTime >= 0) {
        handleRequestPlayTime(player);
      }
      pauseVideo(player);
    }
  }

  if (player.requestPlayTime >= 0) {
    handleRequestPlayTime(player);
  }

  if (player.videoElement.paused && player.playState === PlayState.PLAYING) {
    player.playState = PlayState.PAUSED;
    if (!player.syncedPlayInterupted) {
      player.buttonPlayPause.style = BUTTON_STYLES.PLAY;
    }
    if (
      player.style === 'minimized' &&
      (player.playerSizeState === VideoPlayerSizeState.TO_DEFAULT ||
        player.playerSizeState === VideoPlayerSizeState.DEFAULT)
    ) {
      player.playerSizeState = VideoPlayerSizeState.DEFAULT_LOCKED;
    }
  } else if (!player.videoElement.paused && player.playState !== PlayState.PLAYING) {
    player.playState = PlayState.PLAYING;
    player.buttonPlayPause.style = BUTTON_STYLES.PAUSE;

    if (
      player.style === 'minimized' &&
      (player.playerSizeState === VideoPlayerSizeState.TO_DEFAULT ||
        player.playerSizeState === VideoPlayerSizeState.DEFAULT)
    ) {
      player.playerSizeState = VideoPlayerSizeState.DEFAULT_LOCKED;
      setVisibilityState(player.btnMinimize, VisibilityState.APPEARING);
    }
  }

  if (
    (!player.highlighted && !player.playSlider.knobChanged) ||
    !player.readyToPlaySync ||
    (player.playState === PlayState.PLAYING && player.manager.cursor.idleTime > 3)
  ) {
    if (player.controlsVisible) {
      hideControls(player);
      player.controlsVisible = false;
    }
  } else if (player.highlighted && player.readyToPlaySync) {
    if (!player.controlsVisible) {
      showControls(player);

      showGradient(player.bottomContainer);
      player.controlsVisible = true;
    }
  }

  if (!hasVisibleState(state) && !player.videoElement.paused) {
    player.requestPause;
  }

  if (player.msg) {
    if (player.msgDuration > 0 && player.msgTime >= player.msgDuration) {
      setVideoPlayerMessage(player, '');
    } else {
      player.msgTime += dt;
    }
  }

  player.previousPlayTime = player.currentPlayTime;

  if (player.style === 'minimized') {
    if (player.highlighted) {
      moveEntityToFront(player);
      if (
        player.playerSizeState === VideoPlayerSizeState.MINIMIZED ||
        player.playerSizeState === VideoPlayerSizeState.TO_MINIMIZED
      ) {
        expand(player);
      }
    } else {
      if (
        player.playerSizeState === VideoPlayerSizeState.TO_MINIMIZED ||
        player.playerSizeState === VideoPlayerSizeState.TO_MINIMIZED_FORCED
      ) {
        player.playerSizeState = VideoPlayerSizeState.MINIMIZED;
      } else if (
        player.playerSizeState === VideoPlayerSizeState.DEFAULT ||
        player.playerSizeState === VideoPlayerSizeState.TO_DEFAULT
      ) {
        minimize(player);
      }
    }
  }

  if (player.isDirty) {
    updateDomSize(player);
  }

  return isDirty;
}

function handleRequestPlayTime(player: VideoPlayer) {
  const diff = Math.abs(player.currentPlayTime - player.requestPlayTime);
  if (diff > 0.3) {
    player.videoElement.currentTime = player.requestPlayTime;
    player.lastSeekTime = time;
  }
  player.requestPlayTime = -1;
}

function updateDomSize(vObj: VideoPlayer) {
  const vEl = vObj.element;
  const ent = vObj;
  const [pos, scale] = getAbsolutePosAndScale(ent);

  if (vEl) {
    const w = vObj.width;
    const h = vObj.height;
    vObj.domObj.width = w;
    vObj.domObj.height = h;

    vEl.style.zIndex = ent.localZIndex.toString();
  }
  {
    vObj.domObj.contentWidth = vObj.videoElementWidth + 8;
    vObj.domObj.contentHeight = vObj.videoElementHeight + 8 / vObj.videoAspect;
  }
}

export function handleVideoSyncAction(manager: GameManager, data: JSON_VideoSyncAction) {
  let videoPlayer: VideoPlayer = null;
  for (const vp of getObjectsByType<VideoPlayer>(manager, ObjectType.VIDEOPLAYER)) {
    if (vp.serverObjId === data.sId) {
      videoPlayer = vp;
      break;
    }
  }

  logger.videoplayer('Handle video sync event', { data });

  if (!videoPlayer) {
    console.error('Cannot find video player with this id.', { data });
    return;
  }

  setVideoState(videoPlayer, data.action, data.time, data.msg, data.msgVars);
}

function setVideoState(player: VideoPlayer, state: string, time: number, msg: string, msgVars?: Record<string, string>) {
  switch (state) {
    case 'userPlay':
    case 'userSeekPlay':
    case 'play':
      {
        player.requestPlay = true;
        player.requestPlayTime = time;
        player.msgDuration = 3;
        // videoPlayer.timeSinceStartPlaying = 0;
        if (
          player.style === 'minimized' &&
          (player.playerSizeState === VideoPlayerSizeState.MINIMIZED ||
            player.playerSizeState === VideoPlayerSizeState.TO_MINIMIZED)
        ) {
          expand(player);
        }
      }
      break;
    case 'userPause':
    case 'systemPause':
    case 'userSeekPause':
    case 'pause':
    case 'interupted':
      {
        if (time > 0) {
          if (player.thumbnail) {
            player.thumbnail.disappearDuration = 0;
            setVisibilityState(player.thumbnail, VisibilityState.HIDING);
            setVisibilityState(player.domObj, VisibilityState.APPEARING);
          }
          if (player.tObjTitle) {
            setVisibilityState(player.tObjTitle, VisibilityState.HIDDEN);
          }
          if (player.playIcon) {
            if (hasVisibleState(player.playIcon.visibilityState)) {
              setVisibilityState(player.playIcon, VisibilityState.HIDING);
            }
          }
        }

        player.requestPause = true;
        player.syncedPlayInterupted = false;
        player.localPlayInterupted = false;
        player.requestPlayTime = time;

        if (time === 0 && player.currentPlayTime !== 0) {
          onVideoPlayerEnded(player);
        }

        player.msgDuration = 3;

        // videoPlayer.timeSinceStartPlaying = 0;
      }
      break;
    case 'playInterupted':
      {
        player.syncedPlayInterupted = true;
        player.requestPause = true;
        player.requestPlayTime = time;
        player.msgDuration = -1;
        // videoPlayer.timeSinceStartPlaying = 0;
        // videoPlayer.localPlayMaybeInterupted = false;
      }
      break;
    case 'playContinued':
      {
        player.syncedPlayInterupted = false;
        player.requestPlay = true;
        player.requestPlayTime = time;
        player.msgDuration = 3;
        // videoPlayer.timeSinceStartPlaying = 0;
      }
      break;
    case 'ready':
      {
      }
      break;
    case '':
      {
        player.msgDuration = 3;
      }
      break;
    default:
      console.error('Unkown video sync state', { state });
      return;
  }

  // videoPlayer.timeSinceLocalInterupChange = 0;
  if (msg.length) {
    setVideoPlayerMessage(player, t(msg, msgVars));
    player.msgTime = 0;
  }
}

function showControls(player: VideoPlayer) {
  if (player.buttonPlayPause) {
    setVisibilityState(player.buttonPlayPause, VisibilityState.APPEARING);
  }

  if (player.buttonSubs) {
    setVisibilityState(player.buttonSubs, VisibilityState.APPEARING);
  }

  if (player.playSlider) {
    setVisibilityState(player.playSlider, VisibilityState.APPEARING);
  }

  if (player.tObjTime) {
    setVisibilityState(player.tObjTime, VisibilityState.DEFAULT);
  }

  player.subsElement.classList.add('subtitle-controls');

  if (
    player.btnMinimize &&
    player.style === 'minimized' &&
    player.playerSizeState === VideoPlayerSizeState.DEFAULT_LOCKED
  ) {
    setVisibilityState(player.btnMinimize, VisibilityState.APPEARING);
  }

  if (player.tObjTitle) {
    setVisibilityState(player.tObjTitle, VisibilityState.HIDDEN);
  }

  if (player.showSyncPlayTip && player.playState !== PlayState.PLAYING) {
    showStickyToolTip(
      player,
      [SYNC_TOOLTIP_TEXT],
      'sync_video_tip',
      anchoredPos(v2(0.5, 0.5), v2(-80, 0), v2(1, 0.5)),
      Side.RIGHT,
      0,
      toSize(250, 220),
      () => {
        player.showSyncPlayTip = false;
      },
    );
  }
}

function hideControls(player: VideoPlayer) {
  if (player.buttonPlayPause) {
    setVisibilityState(player.buttonPlayPause, VisibilityState.HIDDEN);
  }

  if (player.buttonSubs) {
    setVisibilityState(player.buttonSubs, VisibilityState.HIDDEN);
  }

  if (player.playSlider) {
    setVisibilityState(player.playSlider, VisibilityState.HIDDEN);
  }

  if (player.tObjTime) {
    setVisibilityState(player.tObjTime, VisibilityState.HIDDEN);
  }

  if (player.playState === PlayState.PLAYING) {
    hideGradient(player.bottomContainer);
  }

  player.subsElement.classList.remove('subtitle-controls');

  if (
    player.playState !== PlayState.PLAYING ||
    player.playerSizeState === VideoPlayerSizeState.TO_MINIMIZED
  ) {
    if (player.tObjTitle && player.currentPlayTime === 0) {
      setVisibilityState(player.tObjTitle, VisibilityState.DEFAULT);
    }
    if (player.playIcon) {
      if (!hasVisibleState(player.playIcon.visibilityState)) {
        setVisibilityState(player.playIcon, VisibilityState.APPEARING);
      }
    }
  }

  if (player.btnMinimize) {
    setVisibilityState(player.btnMinimize, VisibilityState.HIDING);
  }
}

function setTitle(videoPlayer: VideoPlayer, title: string) {
  videoPlayer.title = title;

  let titleToShow = title;

  if (videoPlayer.duration) {
    titleToShow;
  }

  if (videoPlayer.title) {
    if (videoPlayer.tObjTitle) {
      changeTextObjectText(videoPlayer.tObjTitle, titleToShow);
    } else {
      const style = TEXT_STYLE.CONTENT_SUBTITLE;
      const tObj = createAndAddLocalTextObject(
        videoPlayer.manager,
        getZeroStaticEntity(),
        titleToShow,
        style,
        videoPlayer.defaultWidthPx / 100,
      );
      tObj.textShadow = SHADOWS.QUOTE;
      tObj.moveMode = MoveMode.ANCHORED;
      tObj.anchoredPos = anchoredPos(v2(0.5, 0.85), v2(0, 0), v2(0.5, 1));
      setParent(tObj, videoPlayer);
      videoPlayer.tObjTitle = tObj;
    }

    if (
      !videoPlayer.controlsVisible &&
      (videoPlayer.playState === PlayState.PAUSED || videoPlayer.playState === PlayState.NONE)
    ) {
      setVisibilityState(videoPlayer.tObjTitle, VisibilityState.DEFAULT);
      showGradient(videoPlayer.bottomContainer);
    } else {
      setVisibilityState(videoPlayer.tObjTitle, VisibilityState.HIDDEN);
    }
  }
}

function expand(player: VideoPlayer) {
  const videoWidth = player.defaultWidthPx;
  const videoHeight = videoWidth / player.videoAspect;

  clearAnimations(player);

  const animations = createSizeAnimations(
    player,
    { width: videoWidth, height: videoHeight },
    { width: videoWidth, height: videoHeight },
  );

  player.sizeAnimations.push(...animations);
  player.manager.animations.push(...animations);

  player.playerSizeState = VideoPlayerSizeState.TO_DEFAULT;

  if (!player.btnMinimize) {
    player.btnMinimize = createAndAddLocalIconButton(player.manager, BUTTON_STYLES.CLOSE);
    player.btnMinimize.moveMode = MoveMode.ANCHORED;
    player.btnMinimize.anchoredPos = anchoredPos(v2(1, 0), v2(-8, 8), v2(1, 0));
    setParent(player.btnMinimize, player);
    player.btnMinimize.passHover = true;
    player.btnMinimize.disappearDuration = 0;
    setVisibilityState(player.btnMinimize, VisibilityState.HIDDEN);
  }

  if (player.showSubs) {
    player.showSubsTimeout = setTimeout(() => {
      player.showSubsTimeout = null;
      if (player.showSubs) {
        player.subsElement.style.display = '';
      }
    }, 300);
  }
}

function minimize(player: VideoPlayer, force = false) {
  const animations = createSizeAnimations(
    player,
    { width: SIZE_MINIMIZED, height: SIZE_MINIMIZED },
    { width: SIZE_MINIMIZED * player.videoAspect, height: SIZE_MINIMIZED },
  );

  clearAnimations(player);

  player.sizeAnimations.push(...animations);
  player.manager.animations.push(...animations);

  player.playerSizeState = force
    ? VideoPlayerSizeState.TO_MINIMIZED_FORCED
    : VideoPlayerSizeState.TO_MINIMIZED;

  if (player.btnMinimize) {
    setVisibilityState(player.btnMinimize, VisibilityState.HIDING);
  }

  setVideoPlayerMessage(player, '');

  if (player.showSubsTimeout !== null) {
    clearTimeout(player.showSubsTimeout);
    player.showSubsTimeout = null;
  }

  if (player.showSubs) {
    player.subsElement.style.display = 'none';
  }

  hideControls(player);
}

function createSizeAnimations(
  player: VideoPlayer,
  playerTargetSize: Size,
  videoTargetSize: Size,
): SizeAnimation[] {
  const anim1: SizeAnimation = {
    doKill: false,
    delay: 0,
    type: AnimationType.SIZE,
    start: { width: player.width, height: player.height },
    end: playerTargetSize,
    duration: 0.3,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: { width: player.width, height: player.height },
    easeFunction: Ease.outCubic,
    lerpFunction: lerpSize,
    prop: 'size',
  };

  const anim2: SizeAnimation = {
    doKill: false,
    type: AnimationType.SIZE,
    delay: 0,
    start: { width: player.videoElementWidth, height: player.videoElementHeight },
    end: videoTargetSize,
    duration: 0.3,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: { width: player.videoElementWidth, height: player.videoElementHeight },
    easeFunction: Ease.outCubic,
    lerpFunction: lerpSize,
    prop: 'videoSize',
  };

  return [anim1, anim2];
}

function clearAnimations(player: VideoPlayer) {
  for (let i = player.sizeAnimations.length - 1; i >= 0; i--) {
    player.sizeAnimations[i].doKill = true;

    removeDenseArrayIndex(player.sizeAnimations, i);
  }
}

function pauseAndMinimizeOtherVideoPlayers(player: VideoPlayer) {
  for (const otherPlayer of getObjectsByType<VideoPlayer>(player.manager, ObjectType.VIDEOPLAYER)) {
    if (player.id !== otherPlayer.id) {
      if (otherPlayer.playState === PlayState.PLAYING) {
        if (otherPlayer.playState === PlayState.PLAYING) {
          otherPlayer.requestPause = true;
          setVideoPlayerMessage(player, '');
          const send: JSON_VideoSyncAction = {
            sId: otherPlayer.serverObjId,
            action: 'userPause',
            time: player.currentPlayTime,
          };

            }
      }
      if (otherPlayer.style === 'minimized') {
        minimize(otherPlayer);
      }
    }
  }
}

function setThumbnail(videoPlayer: VideoPlayer, imageUri: string) {
  videoPlayer.imageUri = imageUri;

  DataController.cacheImage(videoPlayer.imageUri, videoPlayer.imageUri);
  const im = createImageEnt(
    videoPlayer.manager,
    getZeroStaticEntity({ disableAutoSize: true }),
    videoPlayer.imageUri,
    {
      appearOnLoad: true,
      appearStyle: 'fade',
      maxHeightEm: videoPlayer.height / 100,
    },
  );
  setParent(im, videoPlayer.container, v2(0, 0));
  videoPlayer.thumbnail = im;

  setVisibilityState(videoPlayer.domObj, VisibilityState.HIDING);
}

function createPlayIcon(videoPlayer: VideoPlayer) {
  if (!videoPlayer.playIcon) {
    const im = createImageEnt(videoPlayer.manager, getZeroStaticEntity(), 'iconPlay');
    setParent(im, videoPlayer, v2());
    setVisibilityState(im, VisibilityState.DEFAULT);
    im.width = 14;
    im.height = 14;
    im.radius = 28;
    im.shape = Shape.CIRCLE;
    im.strokeColor = COLORS.WHITE_100;
    im.strokeWeight = 0;
    im.padding = 40;
    videoPlayer.playIcon = im;
    im.backgroundColor = COLORS.NEW_STYLE.BLUE_LIGHT;
  }

  if (videoPlayer.playState !== PlayState.PLAYING) {
    setVisibilityState(videoPlayer.playIcon, VisibilityState.APPEARING);
  }
}

function checkReadyToPlay(player: VideoPlayer) {
  if (player.readyToPlayLocal && player.readyToPlaySync) {
    createPlayIcon(player);
    if (player.currentPlayTime !== 0) {
      if (player.thumbnail) {
        player.thumbnail.disappearDuration = 0;
        setVisibilityState(player.thumbnail, VisibilityState.HIDING);
        setVisibilityState(player.domObj, VisibilityState.APPEARING);
      }

      if (player.playIcon) {
        if (hasVisibleState(player.playIcon.visibilityState)) {
          if (player.playState === PlayState.PLAYING) {
            setVisibilityState(player.playIcon, VisibilityState.HIDING);
          }
        }
      }
    } else {
      setVideoPlayerMessage(player, '');
      if (player.autoPlay) {
        player.requestPlay = true;
      }
    }
  }
}

function handleHLSError(videoPlayer: VideoPlayer, e: HLS.Events.ERROR, data: HLS.ErrorData) {
  switch (data.type) {
    case Hls.ErrorTypes.MEDIA_ERROR:
      console.warn('Media error encountered. Attempting to recover...');

      // This is the key part for your issue
      if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
        logger.warn('Buffer stalled. HLS.js will try to recover.');
      }

      // videoPlayer.hls.recoverMediaError();
      break;
    case HLS.ErrorTypes.NETWORK_ERROR: {
      handleHLSNetworkError(videoPlayer, data);
    }

    default: {
      logger.error('Videoplayer HLS error event', e, data);
    }
  }
}

function handleHLSNetworkError(videoPlayer: VideoPlayer, data: HLS.ErrorData) {
  if (!data.response.code) {
    //unknown network error
  }

  switch (data.response.code) {
    case 0: //no network available
      {
        setVideoPlayerMessage(videoPlayer, 'No connection to video server.');
        videoPlayer.retryLoadSourceTime = 5;
      }
      break;
    case 404: //manifest file not correct
      {
        setVideoPlayerMessage(videoPlayer, 'This video is not available.');
      }
      break;
  }

  const send: JSON_VideoSyncAction = {
    sId: videoPlayer.serverObjId,
    action: 'dataError',
    time: videoPlayer.currentPlayTime,
  };

}

function dispose(player: VideoPlayer) {
  Background.objectReleasePauseBackground(player.id);

  if (player.onVTTTimeUpdate) {
    player.videoElement.removeEventListener('timeupdate', player.onVTTTimeUpdate);
  }

  if (player.hls) {
    player.hls.destroy();
  }
}

function syncLocalPlayInterupted(player: VideoPlayer) {
  const timeSinceLastSeek = time - player.lastSeekTime;
  if (timeSinceLastSeek > 0.5) {
    const send: JSON_VideoSyncAction = {
      sId: player.serverObjId,
      action: 'playInterupted',
      time: player.currentPlayTime,
    };

    player.localPlayInteruptedSynced = true;
  }
}

async function playVideo(player: VideoPlayer) {
  try {
    hideToolTip(player, true, true);
    Background.objectRequestPauseBackground(player.id);
    await player.videoElement.play();
    if (!player.controlsVisible) {
      hideGradient(player.bottomContainer);
    }
  } catch (e) {
    logger.error('error in playing videoElement', e);
  }
}

async function pauseVideo(player: VideoPlayer) {
  try {
    Background.objectReleasePauseBackground(player.id);

    await player.videoElement.pause();
    if (
      player.tObjTitle &&
      !player.controlsVisible &&
      !player.localPlayInteruptedSynced &&
      !player.syncedPlayInterupted
    ) {
    }

    if (player.playIcon) {
      if (!hasVisibleState(player.playIcon.visibilityState)) {
        setVisibilityState(player.playIcon, VisibilityState.APPEARING);
      }
    }
  } catch (e) {
    logger.error('error in pausing videoElement', e);
  }
}

function createPackage(vObj: VideoPlayer, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const state = getEntityState(vObj);

  //render hole only if video is ready
  if (vObj.playerReady && state === VisibilityState.DEFAULT) {
    const pos = addV2(position, v2((-vObj.width / 2) * scale, (-vObj.height / 2) * scale));
    const cRect: RenderPackageClearRect = {
      type: RenderItemType.clearRect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: pos.x,
      positionY: pos.y,
      width: vObj.width,
      height: vObj.height,
      roundedCorners: true,
      cornerRadius1: CORNER_RADIUS,
      cornerRadius2: CORNER_RADIUS,
      cornerRadius3: CORNER_RADIUS,
      cornerRadius4: CORNER_RADIUS,
    };
    rPackage.push(cRect);

    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: vObj.width,
      height: vObj.height,
      shapeStyle: SHAPE_STYLES.NONE,
      cornerRadius1: CORNER_RADIUS,
      cornerRadius2: CORNER_RADIUS,
      cornerRadius3: CORNER_RADIUS,
      cornerRadius4: CORNER_RADIUS,
      shadow: null,
    };
    rPackage.push(rect);
  } else {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: vObj.width,
      height: vObj.height,
      shapeStyle: { ...SHAPE_STYLES.NONE, gradient: true },
      cornerRadius1: CORNER_RADIUS,
      cornerRadius2: CORNER_RADIUS,
      cornerRadius3: CORNER_RADIUS,
      cornerRadius4: CORNER_RADIUS,
      shadow: null,
    };
    rPackage.push(rect);
  }

  return rPackage;
}

function showGradient(container: Container) {
  createOpacityAnimation(container.manager, container, {
    type: AnimationType.OPACITY,
    end: 1,
    duration: 0.3,
    easeFunction: Ease.outCubic,
  });

  // createTransformAnchoredAnimation(container.manager, container, {
  //   type: AnimationType.ANCHORED,
  //   end: anchoredPos(v2(0, 1), v2(0, 0), v2(0, 1)),
  //   duration: 0.3,
  //   easeFunction: EaseFunctions.easeOutCubic,
  // });
}

function hideGradient(container: Container) {
  createOpacityAnimation(container.manager, container, {
    type: AnimationType.OPACITY,
    end: 0,
    duration: 0.3,
    easeFunction: Ease.outCubic,
  });

  // createTransformAnchoredAnimation(container.manager, container, {
  //   type: AnimationType.ANCHORED,
  //   end: anchoredPos(v2(0, 1), v2(0, 0), v2(0, 0)),
  //   duration: 0.3,
  //   easeFunction: EaseFunctions.easeOutCubic,
  // });
}

function setSubsVisibility(player: VideoPlayer, show: boolean, emitToServer: boolean) {
  if (show) {
    player.showSubs = true;
    if (
      player.style !== 'minimized' ||
      player.playerSizeState === VideoPlayerSizeState.DEFAULT ||
      player.playerSizeState === VideoPlayerSizeState.TO_DEFAULT ||
      player.playerSizeState === VideoPlayerSizeState.DEFAULT_LOCKED
    ) {
      player.subsElement.style.display = '';
    } else {
      player.subsElement.style.display = 'none';
    }
    player.buttonSubs.label = t('videoplayer.hide_subtitles');
    player.buttonSubs.style = BUTTON_STYLES.SUBS_HIDE;
  } else {
    player.showSubs = false;
    player.subsElement.style.display = 'none';
    player.buttonSubs.label = t('videoplayer.show_subtitles');
    player.buttonSubs.style = BUTTON_STYLES.SUBS_SHOW;
  }
}
